// Chat turns (D82, docs/12 "Chat with task groups and tasks"): a task group's chat
// conversation (its id is the group's) and a conversation's turns. Written by hand until the
// routes reach the generated schema (S121).
import { queryOptions } from "@tanstack/react-query";

import { requestJson } from "../api/http";
import type { Cli } from "./clis";

const enc = encodeURIComponent;

export type TurnStatus = "queued" | "starting" | "running" | "done" | "failed" | "cancelled";

export interface Turn {
  id: string;
  status: TurnStatus;
  cli: Cli;
  model: string | null;
  stream: boolean;
  /** Null while a task chat's turn waits for a running step to end. */
  run_id: string | null;
  /** The segment the turn's worker opened: where its prompt is encrypted to. */
  segment_id?: string | null;
  created_at: number;
  started_at?: number | null;
  finished_at?: number | null;
  /** Why a failed turn failed, when the server says. */
  reason?: string | null;
}

export interface TurnBody {
  cli: Cli;
  model: string | null;
  stream: boolean;
}

export interface CreatedTurn {
  turn_id: string;
  run_id: string | null;
  status: TurnStatus;
}

export interface ChatConversation {
  id: string;
  kind: string;
}

export const ACTIVE: readonly TurnStatus[] = ["queued", "starting", "running"];

export const groupConversationQuery = (groupId: string) =>
  queryOptions({
    queryKey: ["task-groups", groupId, "conversation"],
    queryFn: async () => (await requestJson("GET", `/api/task-groups/${enc(groupId)}/conversation`)) as ChatConversation,
    staleTime: Infinity,
  });

export const turnsQuery = (conversationId: string) =>
  queryOptions({
    queryKey: ["conversations", conversationId, "turns"],
    queryFn: async () => ((await requestJson("GET", `/api/conversations/${enc(conversationId)}/turns`)) as Turn[] | undefined) ?? [],
    enabled: conversationId !== "",
    // Turn wake-ups are not on the relay yet: poll while one is in flight.
    refetchInterval: (query) => ((query.state.data ?? []).some((t) => ACTIVE.includes(t.status)) ? 2_000 : 15_000),
  });

export const createTurn = async (conversationId: string, body: TurnBody): Promise<CreatedTurn> =>
  (await requestJson("POST", `/api/conversations/${enc(conversationId)}/turns`, body)) as CreatedTurn;

export const cancelTurn = (conversationId: string, turnId: string): Promise<unknown> =>
  requestJson("POST", `/api/conversations/${enc(conversationId)}/turns/${enc(turnId)}/cancel`);
